/* eslint-disable jsx-a11y/no-redundant-roles */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
// CYBSUI
import CybsButton from '@cybs-ui/components/lib/button';
import { StructFlex, StructItem } from '@cybs-ui/components/lib/struct-flex';
import CybsAlert from '@cybs-ui/components/lib/alert';

import UploadTable from './table/index';
import classes from '../component.styles';

// Utility functions
import { fileSize, objectToArray } from '../utils';

const CybsMultiFileUpload = ({
  accept,
  dataTest,
  fileRequirements,
  headingLevel,
  i18n: i18nProp,
  fileValidation,
  maxFiles,
  onRetrieveFiles,
  showStatus
}) => {
  const [isTooMany, setIsTooManyFiles] = React.useState(false);
  const [files, setFiles] = React.useState([]);
  const [isError, setError] = React.useState(false);
  const inputRef = React.useRef();
  const containerRef = React.useRef();

  useEffect(() => {
    onRetrieveFiles(files);
  }, [files, isError, isTooMany]);

  const openFileSelect = () => {
    inputRef.current.click();
  };

  const setAndCheckFiles = newFiles => {
    const invalid = newFiles.some(file => file.error);
    setFiles(newFiles);
    setError(invalid);
  };

  const handleCellChange = (value, cell, rowIdx) => {
    if (cell && cell.stateName === 'visaRemoveRowIcon') {
      // button focusing is being done before the file is removed so accounting for that, there's synchronizaiton issues if focusing is done after deletion
      if (files.length === 1) {
        const nodeList = containerRef.current.querySelectorAll('div button');
        nodeList[nodeList.length - 1].focus();
      } else if ((rowIdx === files.length - 1) && files.length > 1) {
        containerRef.current.querySelectorAll('tbody tr button')[0].focus();
      }

      const newFiles = [...files];
      newFiles.splice(rowIdx, 1);
      setAndCheckFiles(newFiles);
      setIsTooManyFiles(false);
    }
  };

  const handleFileSelection = fileSelection => {
    let filtered = [];
    const fileArray = objectToArray(fileSelection);

    const supplemental = fileArray.map(file => {
      const validatedFile = fileValidation(file);
      const test = validatedFile.valid;
      const tempStatus = test
        ? { status: 'positive', displayName: validatedFile.successMessage }
        : { status: 'negative', displayName: validatedFile.errorMessage };
      return {
        file,
        fileName: file.name,
        fileSize: fileSize(file.size),
        status: {
          status: tempStatus.status,
          displayName: tempStatus.displayName
        },
        error: !validatedFile.valid
      };
    });

    [...files, ...supplemental].forEach(file => {
      if (!filtered.includes(file.file)) {
        filtered.push(file);
      }
    });

    if (filtered.length > maxFiles) {
      filtered = filtered.slice(0, maxFiles);
      setIsTooManyFiles(true);
    } else {
      setIsTooManyFiles(false);
    }
    setAndCheckFiles(filtered);
  };

  const handleDeleteErrors = () => {
    const newFiles = [...files].filter(file => !file.error);
    setAndCheckFiles(newFiles);
    setIsTooManyFiles(false);
  };

  const i18n = {
    ...CybsMultiFileUpload.defaultProps.i18n,
    ...i18nProp
  };

  const errorMessage = () => {
    const message = [];
    if (isError) {
      message.push(<li>{i18n.errorText}</li>);
    }
    if (isTooMany) {
      message.push(<li>{i18n.tooManyFilesMessage}</li>);
    }
    return <ul>{message}</ul>;
  };

  errorMessage.displayName = 'Error Message';

  return (
    <StructFlex direction="column" justify-content="left" ref={containerRef}>
      <h3
        role="heading"
        aria-level={headingLevel}
        css={theme => classes(theme).title}
      >
        {i18n.title}
      </h3>
      <div css={theme => classes(theme).divider}>
        <hr />
      </div>

      <StructFlex direction="column" css={theme => classes(theme).status}>
        <StructItem css={theme => classes(theme).alert}>
          <CybsAlert
            dataTest={`${dataTest}-CybsUpload-errorText`}
            i18n={{ message: errorMessage(), title: '' }}
            isDismissable
            isVisible={isTooMany || (isError && i18n.errorText !== '')}
            isInline
            status="negative"
            onDismiss={() => handleDeleteErrors()}
          />
        </StructItem>
        <p>{i18n.description}</p>
        <span>{fileRequirements && `${i18n.fileRequirements}`}</span>
        <StructFlex direction="column" justify-content="left">
          <div
            id="file-upload"
            style={{
              height: '0px',
              width: '0px',
              padding: '0px',
              border: 'none',
              overflow: 'hidden',
              opacity: 0
            }}
          >
            <input
              tabIndex="-1"
              aria-hidden="true"
              multiple
              ref={inputRef}
              type="file"
              id="file-upload-input"
              data-test={`${dataTest}-CybsUpload-input`}
              onChange={() => handleFileSelection(inputRef.current.files)}
              onClick={e => {
                e.target.value = null;
              }}
              files={files}
              accept={accept}
            />
          </div>
          <UploadTable
            dataTest={`${dataTest}-CybsUpload`}
            i18n={i18n}
            rows={files}
            handleDeleteRow={handleCellChange}
            hidePagination
            showStatus={showStatus}
          />
        </StructFlex>
      </StructFlex>
      <div>
        <CybsButton
          dataTest={`${dataTest}-CybsUpload`}
          i18n={{ label: i18n.uploadFilesButtonLabel }}
          onClick={openFileSelect}
          colorScheme="secondary"
        />
      </div>
    </StructFlex>
  );
};

CybsMultiFileUpload.displayName = 'MultiCybsUpload';

CybsMultiFileUpload.propTypes = {
  /**
   * String with a pattern to be fulfilled by the files
   */
  accept: PropTypes.string,
  /**
   * Data Test
   */
  dataTest: PropTypes.string.isRequired,
  /**
   * File Requirements
   */
  fileRequirements: PropTypes.string,
  /**
   * Function to validate the file input
   */
  fileValidation: PropTypes.func,
  /**
   * Level of the heading displayed before the component
   */
  headingLevel: PropTypes.number,
  /**
   * Function to be executed when the files are retrieved by the browser
   */
  onRetrieveFiles: PropTypes.func,
  /**
   * Max Files
   */
  maxFiles: PropTypes.number,
  /**
   * Localization
   */
  i18n: PropTypes.shape({
    description: PropTypes.string,
    errorButtonLabel: PropTypes.string,
    errors: PropTypes.string,
    errorText: PropTypes.string,
    fileRequirements: PropTypes.string,
    label: PropTypes.string,
    noFilesSelected: PropTypes.string,
    title: PropTypes.string,
    tooManyFilesMessage: PropTypes.string,
    uploadFilesButtonLabel: PropTypes.string,
    tooltipLabel: PropTypes.string,
    headers: PropTypes.shape({
      fileName: PropTypes.string,
      fileSize: PropTypes.string,
      status: PropTypes.string,
      remove: PropTypes.string
    })
  }),
  /**
   * If `true`, the table will show a status.
   */
  showStatus: PropTypes.bool
};

CybsMultiFileUpload.defaultProps = {
  accept: '*',
  fileRequirements: null,
  fileValidation: file => file,
  headingLevel: 3,
  maxFiles: 3,
  i18n: {
    description: '',
    errorButtonLabel: 'Delete Errors',
    errors: 'Errors',
    errorText: '',
    fileRequirements: 'These are file requirements',
    label: 'Upload File(s)',
    noFilesSelected: 'No files Uploaded.',
    title: 'Upload File',
    tooManyFilesMessage: 'Too many files',
    uploadFilesButtonLabel: 'Browse',
    tooltipLabel: 'Remove',
    rowLabel: 'row',
    headers: {
      fileName: 'File Name',
      fileSize: 'File Size',
      status: 'Status',
      remove: 'Remove'
    }
  },
  onRetrieveFiles: () => {},
  showStatus: true
};

export default CybsMultiFileUpload;
